import type { ReactElement } from "react";

import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import type { CenterInvoiceExemption } from "./tenant-types.js";
import { adminCenterApi } from "./urls.js";

/**
 * 保存する前に聞く文（重要な操作は最後にダイアログで確かめる）。<b>請求書が出るか・出なくなるかを言う。</b>
 * どちらかを付けると請求書は 1 通も出なくなり（<b>固定済みの月も誰にも出さない</b>。Implem.Portal.Backend の docs/features/13 §2-2）、
 * 外すと固定していない月の請求書が出る。
 */
export const invoiceExemptionDialog = (title: string, nonMonthly: boolean, freeOfCharge: boolean): string => {
    const marks = [nonMonthly ? "月額以外" : "", freeOfCharge ? "無償利用" : ""].filter((o) => o !== "");
    return marks.length > 0
        ? `「${title}」を請求書を発行しない契約（${marks.join("・")}）にします。`
            + "この契約の請求書は 1 通も発行されなくなります（固定済みの月の請求書も、会員の画面・管理センター・請求の CSV に出なくなります）。よろしいですか？"
        : `「${title}」を請求書を発行しない契約から外します。`
            + "固定していない月の請求書が発行され、会員の画面と請求の CSV に出るようになります。よろしいですか？";
};

/** 印 1 つ分のチェックボックス。<b>外した欄は送られない</b>（API は送られてこなければ外す）。 */
function Mark({ name, label, help, checked, disabled }: {
    readonly name: string;
    readonly label: string;
    readonly help: string;
    readonly checked: boolean;
    readonly disabled: boolean;
}): ReactElement {
    return (
        <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="form-check mt-1" name={name} value="true" defaultChecked={checked} disabled={disabled} />
            <span>
                <span className="font-semibold">{label}</span>
                <span className="block text-xs text-slate-500">{help}</span>
            </span>
        </label>
    );
}

/**
 * 請求書を出さない印（月額以外・無償利用）。<b>請求の権限を持つ運営だけに届く</b>（サーバが決める）。
 * 請求の設定のすぐ下に置く。付け外しは請求書が出る・出なくなる操作なので、送る直前にダイアログで聞く。
 * 解約した契約では変えられない（<c>canChange</c>。サーバが決める）。
 */
export function InvoiceExemptionSection({ tenantId, title, exemption }: {
    readonly tenantId: number;
    /** 契約のシステム名（確認ダイアログの文に入れる）。 */
    readonly title: string;
    readonly exemption: CenterInvoiceExemption;
}): ReactElement {
    const form = useApiForm(adminCenterApi.invoiceExemption(tenantId), undefined, (valueOf) => invoiceExemptionDialog(
        title, valueOf("nonMonthly") === "true", valueOf("freeOfCharge") === "true",
    ));
    const disabled = !exemption.canChange;

    return (
        <section className="card mb-4 p-6" data-invoice-exemption="">
            <h2 className="section-title">請求書を発行しない契約</h2>
            <p className="mb-2 text-sm">
                どちらかにチェックを入れると、<strong>この契約の請求書は 1 通も発行されなくなります</strong>（会員の画面・管理センター・請求の CSV とも）。固定済みの月の請求書も出なくなります（消えはせず、チェックを外すとまた出ます）。
            </p>
            <p className="mb-4 text-sm">
                チェックを外すと、<strong>固定していない月の請求書がまた発行されます</strong>（発行開始月から今月まで。隠していた固定済みの月もまた出ます）。変える前に、下の請求書の欄でどの月が固定済みかを確かめてください。
            </p>
            {disabled ? <p className="alert-warning mb-4 text-sm">解約した契約では、請求書を発行しない契約の設定を変えられません。</p> : null}
            <form method="post" onSubmit={form.onSubmit}>
                <FormSummary messages={form.summary} />
                <div className="mb-4 grid gap-3">
                    <Mark
                        name="nonMonthly"
                        label="月額以外の契約"
                        help="年払いなど、月ごとの請求書を出さずに別に請求する契約。"
                        checked={exemption.nonMonthly}
                        disabled={disabled}
                    />
                    <Mark
                        name="freeOfCharge"
                        label="無償利用"
                        help="無償で使ってもらう契約（請求しない）。"
                        checked={exemption.freeOfCharge}
                        disabled={disabled}
                    />
                </div>
                {disabled ? null : <button type="submit" className="btn-primary btn-sm" disabled={form.busy}>保存する</button>}
            </form>
        </section>
    );
}
